// src/components/SideDrawer.tsx
import { 
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
  Box,
  Avatar,
  Button,
  MenuItem,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import PaidIcon from '@mui/icons-material/Paid';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useState, useRef } from 'react';
import cribCriticLogo from '../assets/CribCriticLogo.svg';
import UsageStatistics from './UsageStatistics';
import { alpha } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';
import Badge from '@mui/material/Badge';
import { useAgentClientChat } from '../contexts/AgentClientChatContext';

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}

const SideDrawer: React.FC<SideDrawerProps> = ({ open, onClose, onOpen }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { hasAgent, agent } = useClient();
  
  // Add safety check for the context
  let agentChatContext: { unreadCount: number; openChat: () => void };
  
  try {
    agentChatContext = useAgentClientChat();
  } catch (error) {
    // Context not available - return safe defaults
    agentChatContext = { unreadCount: 0, openChat: () => {} };
  }
  
  const { unreadCount, openChat } = agentChatContext;
  
  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  
  // This will track if drawer should visually be on top
  const shouldBeOnTop = open || isClosing;

  const handleProfileClick = () => {
    setMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMenuClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  };

  // Create menu items array with conditional business link
  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Homes', icon: <HomeWorkIcon />, path: '/homes' },
    ...(hasAgent && isAuthenticated ? [{ 
      text: 'Chat with Agent', 
      icon: unreadCount > 0 ? (
        <Badge badgeContent={unreadCount} color="error">
          <ChatIcon />
        </Badge>
      ) : <ChatIcon />, 
      path: '/agent-chat' 
    }] : []),
    ...(user?.is_business ? [{ text: 'Business', icon: <BusinessIcon />, path: '/business/admin' }] : []),
    ...(user?.is_agent ? [{ text: 'Agent Portal', icon: <PersonIcon />, path: '/business/agent' }] : []),
    ...(user?.is_admin ? [{ text: 'Admin', icon: <AdminPanelSettingsIcon />, path: '/admin/dashboard' }] : []),
    { text: 'Pricing', icon: <PaidIcon />, path: '/pricing' },

    //{ text: 'Chat', icon: <ChatIcon />, path: '/chat' },
    //{ text: 'History', icon: <HistoryIcon />, path: '/history' },
    //{ text: 'About', icon: <InfoIcon />, path: '/about' },
  ];

  const handleNavigate = (path: string) => {
    if (path === '/agent-chat') {
      openChat(); // Open modal instead of navigating
      onClose();
    } else {
      navigate(path);
      onClose();
    }
  };

  const handleLogin = () => {
    navigate('/login');
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
    onClose();
  };

  const handleProfileNavigation = () => {
    setMenuOpen(false);
    navigate('/profile');
    onClose();
  };

  return (
    <>
      <SwipeableDrawer
        anchor="left"
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        swipeAreaWidth={50} // Increase the swipe area width
        hysteresis={0.52} // Makes it easier to open the drawer
        SwipeAreaProps={{ 'aria-label': 'Swipe area' }}
        SlideProps={{
          onEnter: () => setIsClosing(false),
          onExit: () => setIsClosing(true),
          onExited: () => setIsClosing(false),
        }}
        sx={{ 
          '& .MuiDrawer-paper': { 
            zIndex: shouldBeOnTop ? 3000 : 2001,
            minWidth: 280, // Add minimum width to ensure consistent drawer size
            width: 280     // Fixed width for consistency
          },
          zIndex: shouldBeOnTop ? 3000 : 2001
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%'
        }}>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <img 
              src={cribCriticLogo} 
              alt="CribCritic Logo" 
              style={{ height: '32px', width: '32px' }} 
            />
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              CribCritic
            </Typography>
          </Box>
          <Divider />
          
          {/* Main menu items */}
          <List sx={{ width: '100%', flexGrow: 1, py: 0 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigate(item.path)}
                  sx={{ 
                    py: 1.5,
                    '&:hover': { 
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, '& .MuiSvgIcon-root': { fontSize: 24 } }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    slotProps={{
                      primary: { 
                        fontSize: '1.1rem',
                        fontWeight: 'medium'
                      }
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider />

          {/* for debugging purposes */}
          {/* <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Typography variant="caption" color="text.secondary">
              User: {JSON.stringify(user)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Authenticated: {isAuthenticated ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Agent: {hasAgent ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="caption" color="text.secondary">

              Agent Info: {JSON.stringify(agent)}
            </Typography>
          </Box> */}

          {/* Agent Information - Only show for authenticated users with an agent */}
          {isAuthenticated && hasAgent && agent && (
            <>
              <Box sx={{ p: 2 }}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    bgcolor: theme => alpha(
                      theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100], 
                      0.5
                    ),
                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme => `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => window.location.href = `/agent/${encodeURIComponent(agent.email)}`}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar 
                        src={agent.picture} 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          mr: 1.5,
                          bgcolor: 'primary.main'
                        }}
                      >
                        {agent.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                          Your Agent
                        </Typography>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          {agent.name}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {agent.email && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="block" 
                        gutterBottom
                      >
                        {agent.email}
                      </Typography>
                    )}
                    
                    {agent.phone && (
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {agent.phone}
                      </Typography>
                    )}
                    
                    <Chip 
                      label={agent.businessName}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </CardContent>
                </Card>
              </Box>
              <Divider />
            </>
          )}

          {/* Usage Statistics - Only show for authenticated users */}
          {isAuthenticated && (
            <>
              <UsageStatistics />
              <Divider />
            </>
          )}
          
          {/* User profile section */}
          <Box sx={{ p: 2 }}>
            {isAuthenticated ? (
              <>
                <Box 
                  ref={anchorRef}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    cursor: 'pointer',
                    p: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={handleProfileClick}
                  aria-controls={menuOpen ? "profile-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? "true" : undefined}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Avatar src={user?.picture} alt={user?.username} sx={{ width: 40, height: 40 }} />
                    {user?.subscription_tier === 'pro' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid white',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          zIndex: 1,
                        }}
                      >
                        <StarIcon sx={{ fontSize: '10px', color: 'white' }} />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {user?.name || user?.username}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      noWrap 
                      sx={{ 
                        maxWidth: '210px',
                      }}
                    >
                      {user?.email || user?.username}
                    </Typography>
                  </Box>
                  <KeyboardArrowDownIcon fontSize="small" color="action" />
                </Box>
                <Popper
                  open={menuOpen}
                  anchorEl={anchorRef.current}
                  placement="top-end"
                  transition
                  disablePortal
                  style={{ zIndex: 2500 }}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'bottom right' }}
                    >
                      <Paper elevation={3} sx={{ width: 200 }}>
                        <ClickAwayListener onClickAway={handleMenuClose}>
                          <MenuList
                            autoFocusItem={menuOpen}
                            id="profile-menu"
                          >
                            <MenuItem onClick={handleProfileNavigation}>
                              <ListItemIcon>
                                <AccountCircleOutlined fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Profile</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>
                              <ListItemIcon>
                                <LogoutIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Logout</ListItemText>
                            </MenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={handleLogin}
                startIcon={<LoginIcon />}
                fullWidth
              >
                Sign in
              </Button>
            )}
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default SideDrawer;